import {
  AfterViewInit,
  ChangeDetectorRef,
  Component, ElementRef, EventEmitter,
  Input,
  OnChanges,
  OnInit, Output,
  QueryList,
  SimpleChanges,
  ViewChildren
} from '@angular/core';
import {TimePickerComponent} from '@niceltd/sol-angular/time-picker';
import {DateUtils} from '../../../../common/utils/date';
import {TranslateService} from '@ngx-translate/core';

const now = new Date();

@Component({
  standalone: false,
  selector: 'esp-operating-hours',
  templateUrl: './operating-hours.component.html',
  styleUrls: ['./operating-hours.component.css']
})
export class OperatingHoursComponent implements OnInit,OnChanges, AfterViewInit{
  @ViewChildren(TimePickerComponent) timePickers: QueryList<TimePickerComponent>;
  @Input() operatingHours;
  @Output() modelChanged = new EventEmitter<{model:any,isValid:boolean}>();

  startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0);
  endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 45);
  days = {
    "SUNDAY": {value: '00:00', isValid: true},
    "MONDAY": {value: '00:00', isValid: true},
    "TUESDAY": {value: '00:00', isValid: true},
    "WEDNESDAY": {value: '00:00', isValid: true},
    "THURSDAY": {value: '00:00', isValid: true},
    "FRIDAY": {value: '00:00', isValid: true},
    "SATURDAY": {value: '00:00', isValid: true}
  }
  dayNames = Object.keys(this.days)
  constructor(private cdr: ChangeDetectorRef, private elRef: ElementRef, private translate: TranslateService) { }

  ngOnInit(): void {
  }

  onTimeChanged($event: any, day:string) {
    const picker = this.timePickers.find(tp => tp.name === day);
    if (picker) {
      this.validatePicker(picker, picker.selectedTimeString || '');
    }
  }

  validatePicker(timePicker: TimePickerComponent, value: string) {
    const isValid = this.isValidTime(value);
    if (isValid) {
      this.days[timePicker.name].value = value;
    }
    this.days[timePicker.name].isValid = isValid;

    // Directly toggle error-border class on the SOL input container
    const container = timePicker.elementRef.nativeElement.querySelector('.sol-input-container');
    if (container) {
      if (!isValid) {
        container.classList.add('error-border');
      } else {
        container.classList.remove('error-border');
      }
    }

    this.notifyModelChange();
    this.cdr.detectChanges();
    // Keep the combobox accessible name in sync with the new value/status so
    // JAWS announces the updated "(Closed)/(Partial)/..." state (WCAG 4.1.2).
    this.updateAriaLabel(timePicker);
  }

  isValidTime(value: string): boolean {
    if (!value || value.trim() === '') return false;
    if (!/^\d{2}:\d{2}$/.test(value)) return false;
    const [hours, minutes] = value.split(':').map(Number);
    if (hours > 24) return false;
    if (hours === 24 && minutes > 0) return false;
    if (minutes < 0 || minutes >= 60) return false;
    return true;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if(changes.operatingHours){
      this.setOperatingHours();
    }
  }
  ngAfterViewInit() {
    // NOTE: with @niceltd/sol@16 we monkey-patched the (global) prototype
    // TextInputComponent.prototype.onInputContainerClick to a no-op, because a click
    // on the trailing clock button bubbled to the field container and SOL refocused
    // the input. @niceltd/sol-angular (CXAPP-33285) now ignores container clicks
    // that originate from a trailing <button> (the clock icon renders one), and the
    // remaining container area has pointer-events: none in our CSS, so the patch
    // is no longer needed and has been removed.

    this.timePickers.toArray().forEach((timePicker) => {
      // SOL generates options using DatePipe which uses 12-hour format for 'hh:mm'.
      // Replace with full 00:00–24:00 range in 15-min increments.
      timePicker.timeOptions = [];
      for (let h = 0; h <= 23; h++) {
        for (let m = 0; m < 60; m += 15) {
          timePicker.timeOptions.push(
            `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
          );
        }
      }
      timePicker.timeOptions.push("24:00");

      // Override to prevent SOL from resetting selectedTimeString or stealing focus
      (timePicker as any).validateAndUpdateTouched = () => {};
      (timePicker as any).onFocusOut = () => {};
      // Prevent SOL's focusInput from refocusing after dropdown close
      (timePicker as any).focusInput = () => {};

      // Override SOL's onChange to prevent auto-conversion of partial input.
      // Only accept exact NN:NN format; otherwise mark invalid.
      timePicker.onChange = (searchText: string) => {
        timePicker.selectedTimeString = searchText;
        this.validatePicker(timePicker, searchText);
      };

      // Override selectedTime to keep focus on the input after dropdown selection
      const origSelectedTime = timePicker.selectedTime.bind(timePicker);
      timePicker.selectedTime = (time: string) => {
        origSelectedTime(time);
        // @niceltd/sol-angular only emits (timeChanged) when the string resolves to
        // one of SOL's own generated options (it sets date_object); @niceltd/sol@16
        // emitted unconditionally. Our replaced option list contains values SOL
        // cannot resolve (e.g. "23:45", "24:00"), so validate here when SOL did not
        // emit — otherwise onTimeChanged already ran via (timeChanged).
        if (!(timePicker as any).date_object) {
          this.validatePicker(timePicker, timePicker.selectedTimeString || '');
        }
        setTimeout(() => {
          const inputEl = timePicker.elementRef.nativeElement.querySelector('input') as HTMLInputElement;
          if (inputEl) {
            // Call the native focus directly, bypassing our allowFocus guard
            HTMLElement.prototype.focus.call(inputEl);
          }
        }, 50);
      };

      // Listen on native input for deletions and focus management
      setTimeout(() => {
        const inputEl = timePicker.elementRef.nativeElement.querySelector('input') as HTMLInputElement;
        const container = timePicker.elementRef.nativeElement.querySelector('.sol-input-container');

        if (inputEl) {
          // WCAG 1.3.1 / 4.1.2: give the combobox an accessible name that also
          // conveys the column header ("TOTAL OPEN HOURS (hh:mm)") and the
          // current status ("(Closed)", "(Partial)", ...) that are only shown
          // visually in adjacent cells, so JAWS announces the full context.
          this.updateAriaLabel(timePicker);

          // WCAG 2.1.1 Keyboard: SOL renders the trailing clock control as its
          // own <button class="sol-icon-button" aria-label="Select time">. Keep
          // it in the tab order so keyboard users can Tab to the clock icon and
          // open the time list via Enter/Space (SOL's directive already binds
          // keydown.enter/space -> toggleTimePicker on the button). SOL already
          // supplies the button's accessible name, so we only restore focus.
          const clockBtn = timePicker.elementRef.nativeElement.querySelector('.sol-icon-button');
          if (clockBtn) {
            clockBtn.removeAttribute('aria-hidden');
            clockBtn.setAttribute('tabindex', '0');
          }

          // Replace focus() to block SOL's programmatic refocus calls.
          // Only allow focus from trusted user interactions (click/tab).
          let allowFocus = false;
          const origFocusFn = inputEl.focus.bind(inputEl);
          inputEl.focus = function() {
            if (allowFocus) {
              origFocusFn();
            }
          };
          inputEl.addEventListener('mousedown', () => { allowFocus = true; });
          inputEl.addEventListener('mouseup', () => { setTimeout(() => { allowFocus = false; }); });
          // Allow keyboard tab focus
          inputEl.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key === 'Tab') { allowFocus = true; setTimeout(() => { allowFocus = false; }); return; }
            // WCAG 2.1.1 / 4.1.2 (ARIA combobox pattern): ArrowDown / ArrowUp must
            // OPEN the time list when it is closed. SOL only navigates an already
            // open list from these keys, and the trailing clock button is
            // deliberately removed from the tab order (see clockBtn handling
            // above), so keyboard users otherwise had no way to open the dropdown.
            // Open it on the first arrow press and move focus onto the selected
            // option so subsequent arrow keys navigate the list naturally.
            //
            // @niceltd/sol-angular uses the aria-activedescendant pattern: DOM focus
            // stays on the input, the list scrolls to the selected option when it
            // opens, and SOL's own (keydown.arrowdown/arrowup) handler moves the
            // active option on later presses. TimePickerListComponent's
            // setFocusOnSelectedElement() (which we used to call here) was removed.
            // Stop propagation so SOL's handler on <sol-text-input> doesn't also
            // treat this "open" keypress as a "move to next option" keypress.
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
              const directive = (timePicker as any).timePickerDirective;
              if (directive && !directive.isTimePickerOpen) {
                e.preventDefault();
                e.stopPropagation();
                directive.openTimepicker();
              }
            }
          });

          inputEl.addEventListener('input', () => {
            const value = inputEl.value || '';
            timePicker.selectedTimeString = value;
            this.validatePicker(timePicker, value);
          });
          inputEl.addEventListener('focus', () => {
            container?.classList.add('esp-focused');
          });
          inputEl.addEventListener('blur', () => {
            container?.classList.remove('esp-focused');
          });
        }
      });
    });
  }

  setOperatingHours() {
    if(this.operatingHours){
      Object.keys(this.days).forEach((day)=>{
        let isMultiValue = this.operatingHours[day] ==="--:--";
        this.days[day].value = isMultiValue?this.operatingHours[day]:DateUtils.convertMinutesToTimeString(this.operatingHours[day]);
        this.days[day].isValid = true;
      });
      setTimeout(()=>{
        this.timePickers.toArray().forEach((timePicker)=>{
          timePicker.selectedTimeString = this.days[timePicker.name].value;
          const container = timePicker.elementRef.nativeElement.querySelector('.sol-input-container');
          container?.classList.remove('error-border');
          (timePicker as any).cdr.detectChanges();
          // Refresh the accessible name so the announced status matches the
          // freshly loaded value (WCAG 4.1.2).
          this.updateAriaLabel(timePicker);
        });
      },0)
    }
  }



  notifyModelChange() {
    let isValid = true;
    let model = {};
    Object.keys(this.days).forEach((day)=>{
      isValid = isValid && this.days[day].isValid;
      model[day]=this.days[day].value;
      if(model[day]!=="--:--" && isValid){
        model[day]=DateUtils.convertTimeStringToMinutes(model[day]);
      }
    });
    this.modelChanged.emit({model,isValid});
  }

  /**
   * Status text shown visually beside each row ("(Closed)", "(Partial)", ...).
   * Mirrors the *ngIf conditions in the template so the combobox aria-label can
   * convey the same information to screen readers (WCAG 1.3.1).
   */
  private getStatusText(day: string): string {
    const dayState = this.days[day];
    if (!dayState) return '';
    if (dayState.isValid === false) {
      return this.translate.instant('plan.ct.settings.operating.hours.invalid');
    }
    switch (dayState.value) {
      case '00:00':
        return this.translate.instant('plan.ct.settings.operating.hours.closed');
      case '--:--':
        return this.translate.instant('plan.ct.settings.multiple.values');
      case '24:00':
        return this.translate.instant('plan.ct.settings.operating.hours.open');
      default:
        return this.translate.instant('plan.ct.settings.operating.hours.partial');
    }
  }

  /**
   * Builds the combobox accessible name so JAWS announces the day, the parent
   * column header ("TOTAL OPEN HOURS (hh:mm)") and the current status together
   * (WCAG 1.3.1 / 4.1.2).
   */
  private updateAriaLabel(timePicker: TimePickerComponent): void {
    const inputEl = timePicker.elementRef.nativeElement.querySelector('input') as HTMLInputElement;
    if (!inputEl) return;
    const dayLabel = this.translate.instant('plan.ct.settings.operating.hours.' + timePicker.name.toLowerCase());
    const columnLabel = this.translate.instant('plan.ct.settings.operating.hours.total.column');
    const status = this.getStatusText(timePicker.name);
    const parts = [dayLabel, columnLabel, status].filter(Boolean);
    inputEl.setAttribute('aria-label', parts.join(', '));
  }
}
